import { Chip, Tooltip, Typography } from '@material-ui/core';
import { TableColumn } from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';

// The default component table (name, system, owner, type, lifecycle,
// description, tags) overflows the card at laptop widths and hides the actions
// column. System repeats owner for our entities, so it goes. Lifecycle shares
// the type cell and is shown only when it is not production, so the exceptions
// (experimental, deprecated) stand out. Tags cap at two so every row stays one
// line.
const MAX_TAGS = 2;

const nowrap = { whiteSpace: 'nowrap' as const };

const typeColumn: TableColumn<CatalogTableRow> = {
  title: 'Type',
  field: 'entity.spec.type',
  cellStyle: nowrap,
  render: ({ entity }) => {
    const lifecycle = entity.spec?.lifecycle;
    return (
      <>
        {String(entity.spec?.type ?? '')}
        {lifecycle && lifecycle !== 'production' && (
          <Typography
            component="span"
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: 6 }}
          >
            {String(lifecycle)}
          </Typography>
        )}
      </>
    );
  },
};

const chipStyle = { margin: 2 };

const tagsColumn: TableColumn<CatalogTableRow> = {
  ...CatalogTable.columns.createTagsColumn(),
  cellStyle: { ...nowrap, padding: '0 16px' },
  render: ({ entity }) => {
    const tags = entity.metadata.tags ?? [];
    const rest = tags.slice(MAX_TAGS);
    return (
      <>
        {tags.slice(0, MAX_TAGS).map(tag => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            variant="outlined"
            style={chipStyle}
          />
        ))}
        {rest.length > 0 && (
          <Tooltip title={rest.join(', ')}>
            <Chip
              label={`+${rest.length}`}
              size="small"
              variant="outlined"
              style={chipStyle}
            />
          </Tooltip>
        )}
      </>
    );
  },
};

// Backstage's Table mutates `cellStyle` of highlighted columns in place, so
// each column gets its own object rather than a shared constant.
export const compactColumns = (
  kind: string,
): TableColumn<CatalogTableRow>[] => [
  {
    ...CatalogTable.columns.createNameColumn({ defaultKind: kind }),
    width: '200px',
    cellStyle: { ...nowrap },
  },
  { ...CatalogTable.columns.createOwnerColumn(), cellStyle: { ...nowrap } },
  { ...typeColumn, width: '170px' },
  {
    ...CatalogTable.columns.createMetadataDescriptionColumn(),
    cellStyle: { minWidth: 240 },
  },
  tagsColumn,
];

export const catalogColumns: CatalogTableColumnsFunc = context => {
  const kind =
    context.entities[0]?.kind?.toLocaleLowerCase('en-US') ??
    context.filters.kind?.value;
  return kind === 'component' || kind === 'api'
    ? compactColumns(kind)
    : CatalogTable.defaultColumnsFunc(context);
};
